import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { apiUrl } from '../config/api';
import {
  computeGalleryLayout,
  isIntegerPixelScale,
  isNativeGalleryMode,
} from '../config/gallery';

function GalleryImageCard({
  img,
  displayPanel,
  formatImageDateTime,
  formatCreateTime,
  formatFileSize,
}) {
  const nativeMode = isNativeGalleryMode(displayPanel?.displayMode);
  const integerScale =
    nativeMode &&
    (displayPanel?.integerScale ??
      isIntegerPixelScale({
        nativeWidth: displayPanel?.nativeWidth,
        nativeHeight: displayPanel?.nativeHeight,
        width: displayPanel?.width,
        height: displayPanel?.height,
      }));

  return (
    <div className="gallery-card gallery-card--image">
      <a
        href={apiUrl(img.filePath)}
        target="_blank"
        rel="noopener noreferrer"
        className="gallery-image-link"
      >
        <div className="gallery-image-frame">
          <img
            src={apiUrl(img.filePath)}
            alt={img.fileName}
            className={`gallery-image${integerScale ? ' gallery-image--pixel-scale' : ''}`}
            loading="lazy"
            width={displayPanel?.nativeWidth ?? displayPanel?.width}
            height={displayPanel?.nativeHeight ?? displayPanel?.height}
          />
        </div>
      </a>
      <div className="gallery-info">
        {nativeMode ? (
          <>
            <div className="gallery-filename" title={img.fileName}>{img.fileName}</div>
            <div className="gallery-meta gallery-meta--native">{formatImageDateTime(img.createTime)} 大小：{formatFileSize(img.size)}</div>
          </>
        ) : (
          <>
            <h3 className="gallery-filename" title={img.fileName}> {img.fileName}</h3>
            <div className="gallery-meta">创建时间：{formatCreateTime(img.createTime)}</div>
            <div className="gallery-meta">大小：{formatFileSize(img.size)}</div>
          </>
        )}
      </div>
    </div>
  );
}

function GalleryVirtualGrid({
  images,
  panel,
  formatImageDateTime,
  formatCreateTime,
  formatFileSize,
}) {
  const parentRef = useRef(null);
  const [layout, setLayout] = useState({ width: 0, scrollMargin: 0 });

  const measureLayout = () => {
    const el = parentRef.current;
    if (!el) return;
    setLayout({
      width: el.clientWidth,
      scrollMargin: el.offsetTop,
    });
  };

  useLayoutEffect(() => {
    measureLayout();
    const el = parentRef.current;
    if (!el) return undefined;

    const ro = new ResizeObserver(measureLayout);
    ro.observe(el);
    window.addEventListener('resize', measureLayout);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureLayout);
    };
  }, [images.length]);

  const gridLayout = useMemo(
    () => computeGalleryLayout(panel, layout.width),
    [panel, layout.width]
  );

  const columnCount = gridLayout?.columnCount ?? 1;
  const cardWidth = gridLayout?.cardWidth ?? panel?.cardWidth ?? 270;
  const cardHeight = gridLayout?.cardHeight ?? panel?.cardHeight ?? 430;
  const columnGap = gridLayout?.columnGap ?? 18;
  const rowGap = gridLayout?.rowGap ?? 15;
  const displayPanel = gridLayout?.displayPanel ?? panel;

  const rowCount = Math.ceil(images.length / columnCount);
  const rowHeight = cardHeight + rowGap;

  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeight,
    overscan: 2,
    scrollMargin: layout.scrollMargin,
  });

  const gridStyle = gridLayout?.cssVars ?? undefined;

  return (
    <div
      ref={parentRef}
      className="gallery-virtual-grid gallery-grid gallery-grid--images"
      style={gridStyle}
    >
      <div
        className="gallery-virtual-grid__inner"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const startIndex = virtualRow.index * columnCount;
          const rowImages = images.slice(startIndex, startIndex + columnCount);

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              className="gallery-virtual-row"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                maxWidth: '100%',
                height: `${rowHeight}px`,
                transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'flex-start',
                gap: `${columnGap}px`,
                paddingRight: `${columnGap}px`,
                overflow: 'visible',
                boxSizing: 'border-box',
              }}
            >
              {rowImages.map((img) => (
                <div
                  key={img.filePath || img.fileName}
                  className="gallery-virtual-grid__cell"
                  style={{
                    flex: `0 0 ${cardWidth}px`,
                    width: `${cardWidth}px`,
                    minWidth: `${cardWidth}px`,
                    maxWidth: `${cardWidth}px`,
                  }}
                >
                  <GalleryImageCard
                    img={img}
                    displayPanel={displayPanel}
                    formatImageDateTime={formatImageDateTime}
                    formatCreateTime={formatCreateTime}
                    formatFileSize={formatFileSize}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GalleryVirtualGrid;
